<template>
  <v-card flat class="container--fluid">

    <v-card flat class="container--fluid bg-gray no-margins elevation-1 not-rounded">
      <v-card flat class="v-card__container">
        <v-card-title class="no-margins">
          <h4>Statistik</h4>
          <v-spacer></v-spacer>
					<v-select
          	:items="statistics"
          	label="Välj statistik"
						@change="setStatistic"
        	></v-select>
				</v-card-title>
				<v-card-text v-if="!loading">
					<component v-bind:is="currentCriteriaComponent" 
						@reset="resetData" 
						@fetch="loadData"
						@export="exportData" 
						@tableConfig="configureTable"
						@setHeaders="configureHeaders"
						:schools="schools"
					></component>
				</v-card-text>
			</v-card>
		</v-card>

		<v-card flat class="container--fluid ma-0 pa-0">
			<v-data-table
				v-model="statisticTable"
				:headers="statisticHeaders"
				:items="statisticItems"
				:options.sync="statisticOptions"
				:sort-by="this.statisticOptions.sortBy"
				:sort-desc="true"
				:server-items-length="totalRows"
				:items-per-page="itemsPerPage"
				item-value="name"
				:class="'ma-6'"
				:loading="isLoading"
				loading-text="Laddar statistikdata"
				loader-height="4"
			>
				<template v-slot:top >
					<div class="d-flex justify-end align-center pr-2">
						<div class="mr-8">
							{{!totalRows ? '-' : $vuetify.lang.t('$vuetify.dataFooter.pageText', itemStart, itemStop	, totalRows)}}
						</div>
						<div class="mr-2">
							<v-btn icon text 
								@click="onPreviousPage"
								:disabled="disablePreviousPageIcon"
							>
								<v-icon>$prev</v-icon>
							</v-btn>
						</div>
						<div class="ml-2">
							<v-btn icon text 
								@click="onNextPage"
								:disabled="disableNextPageIcon"
							>
								<v-icon>$next</v-icon>
							</v-btn>
						</div>
					</div>
				</template>

				<template v-for="header in statisticHeaders.filter((header) => header.hasOwnProperty('formatter'))" v-slot:[`item.${header.value}`]="{ header, value }">
					{{ header.formatter(value) }}
				</template>

				<template v-slot:[`item.person.fullName`]="{item}">
					<template v-if="item.person.id">
						<router-link :to="'/people/' + item.person.id">
							{{item.person.fullName}}
						</router-link>
					</template>
					<template v-else>
						{{item.person.fullName}}
					</template>
				</template>
			</v-data-table>

			<v-overlay :value="isLoading" absolute :opacity="0.2"/>
		</v-card>
	</v-card>
</template>

<script>
//- Import services and utitlity functions
import get from 'lodash/get'
import {pageSize} from '@/store/constants'
import { mapGetters } from 'vuex'
import API from '@/services/api.js'
const SugarDate = require('sugar/date')

//- Import download-mixin for excel expoty
import {DownloadsMixin} from '@/components/mixins'

//- Import criterias and settings for the different statistics
import NoStatistics from '@/components/statistics/NoStatistics'
import GUStatistics from '@/components/statistics/GUStatistics'
import KtidStatistics from '@/components/statistics/KtidStatistics'
import GUNeedCompletionStatistics from '@/components/statistics/GUNeedCompletionStatistics'
import GUNeedCompleteEducationStatistics from '@/components/statistics/GUNeedCompleteEducationStatistics'
import TCStatistics from '@/components/statistics/TCStatistics'
import TimeQTStatistics from '@/components/statistics/TimeQTStatistics'
import TimeBEStatistics from '@/components/statistics/TimeBEStatistics'
import GUKTStatistics from '@/components/statistics/GUKTStatistics'
import ApplicationStatistics from '@/components/statistics/ApplicationStatistics'
import DeviationsBEStatistics from '@/components/statistics/DeviationsBEStatistics'
import DeviationsQTStatistics from '@/components/statistics/DeviationsQTStatistics'

export default {
	//- Mixin
	mixins: [DownloadsMixin],

	//- data
	data: () => ({
		statistics: [{
			text: 'Personer i Grundutbildning',
			value: {name: 'GU', cmp: GUStatistics}
		},{
			text: 'Personer i Grundutbildning med kompletteringsbehov',
			value: {name: 'GUC', cmp: GUNeedCompletionStatistics}
		},{
			text: 'Personer i Grundutbildning med behov av komplett utbildning',
			value: {name: 'GUCE', cmp: GUNeedCompleteEducationStatistics}
		},{
			text: 'Personer i Kvalificeringstid',
			value: {name: 'Ktid', cmp: KtidStatistics}
		},{
			text: 'Utfärdade yrkesbevis',
			value: {name: 'TC', cmp: TCStatistics }
		},{
			text: 'Personer i Grundutbildning och Kvalificeringstid',
			value: {name: 'GUKT', cmp: GUKTStatistics }
		},{
			text: 'Tid i kvalificeringstid',
			value: { name: 'TimeQT', cmp: TimeQTStatistics }
		},{
			text: 'Tid i grundutbildningen',
			value: { name: 'TinBE', cmp: TimeBEStatistics}
		},{
			text: 'Anmälningar',
			value: { name: 'APP', cmp: ApplicationStatistics}
		},{
			text: 'Grundutbildningens avvikelser',
			value: { name: 'devBE', cmp: DeviationsBEStatistics}
		},{
			text: 'Avvikelser i kvalificeringstid',
			value: {name: 'devQT', cmp: DeviationsQTStatistics}
		}],
		statisticSelected: {name: 'None', cmp: NoStatistics},
		statisticTable: [],
		statisticHeaders: [],
		statisticItems: [],
		statisticOptions: {},
		statistcsEndpoint: null,
		statisticCriteria: {},
		inited: false,
		itemsPerPage: pageSize,
		totalRows: 0,
		clickedSearch: false,
		dataFetched: false,
		loading: false,
		schools: [],
	}),

	//- Watch data-table options to detect page changes and refresh data-items
  watch: {
    statisticOptions: {
      handler() {
        if (this.inited) {
          this.getData()
        }
      },
      deep: true
    }
  },

	//- initied is set on creation to stop watcher from triggering before we are created
	created() {
		this.inited = true
		this.loadSchool()
	},

	// add stuff from store and a computed to handle statistic component swapping
	computed: {
		...mapGetters('app', ['isLoading']),

		currentCriteriaComponent() {
			return get(this, 'statisticSelected.cmp', null)
		},
		disableNextPageIcon(){
			return this.statisticOptions.itemsPerPage <= 0 ||
        this.statisticOptions.page * this.statisticOptions.itemsPerPage >= this.totalRows
		},
		disablePreviousPageIcon(){
			return this.statisticOptions.page < 2 || this.totalRows === 0
		},
		itemStart(){
			return (this.statisticOptions.page - 1) * this.statisticOptions.itemsPerPage + 1
		},
		itemStop(){
			const pageStop = (this.statisticOptions.page - 1) * this.statisticOptions.itemsPerPage + this.statisticOptions.itemsPerPage

			return pageStop > this.totalRows ? this.totalRows : pageStop
		}
	},

	//- the methods
	methods: {
		loadSchool(){
			this.loading = true;
			API.listSchool()
				.then(res => { 
					this.schools = Object.entries(res.data).reduce((acc, [region, regionData]) => {
						const regionItems = Object.entries(regionData).reduce((accRegionData, [municipality, municipalitySchools]) => {
							const group = `${region} - ${municipality}`;
							return [
								...accRegionData,
								{ header: group},
								...municipalitySchools.map(s => ({ value: s.id, text: s.name, group })),
								{ divider: true },
							]
						}, [])
						return [ ...acc, ...regionItems]	
					}, []) 
				})
				.finally(() => { 
					this.loading = false 
				})
		},

		onPreviousPage(){
			this.statisticOptions.page = this.statisticOptions.page - 1
		},

		onNextPage(){
			this.statisticOptions.page = this.statisticOptions.page + 1
		},

		//- This is called when selected item in statistics-list is changed
		setStatistic(item) {
			this.statisticSelected = item
			this.resetData()
		},

		//- this is called when a new statistic component is initiated
		configureTable(config) {
			this.statisticHeaders = get(config, 'headers', [])
			this.statisticOptions = get(config, 'options', {})		
			this.statisticListEndpoint = get(config, 'listEndpoint', null)		
			this.statisticExportEndpoint = get(config, 'exportEndpoint', null)		
			this.statisticCriteria = {}
		},

		configureHeaders(headers) {
			this.statisticHeaders = headers;
		},

		//- Called when user presses "Search" in statistics criteria
		loadData(value) {
			if (!this.statisticListEndpoint) {
				this.$events.fire('showError', 'Kunde inte hämta statistiken (noEndpoint)')
				return
			}

			this.clickedSearch = true
			this.dataFetched = false
			this.statisticCriteria = value

			//- if we're not on page 1, just changing the options to page 1 will trigger a search
			//- by the watcher. Done this way to prevent two initial searches
			if (this.statisticOptions.page > 1) {
				this.statisticOptions.page = 1
			} else {
				this.getData()
			}
		},

		//- Called when user presses "Reset" in statistics criteria
		resetData() {
			this.dataFetched = false
			this.statisticCriteria = {}
			this.statisticItems = []
			this.totalRows = 0
			this.statisticOptions.page = 1
		},

		//- Function to export data to excel
		async exportData() {
			if (!this.totalRows) {
				this.$events.fire('showError', 'Kunde inte exportera statistiken (noRows)')
				return
			}
			if (!this.statisticExportEndpoint) {
				this.$events.fire('showError', 'Kunde inte exportera statistiken (noEndpoint)')
				return
			}

			const { sortBy, sortDesc } = this.statisticOptions

			const criteria = {
				page: 1,
				startAt: 0,
				sortBy: sortBy[0],
				sortDesc: sortDesc[0],
				searchParameters: this.statisticCriteria,
				pageSize: 1000000,
						toExcel: {
							headers: this.statisticHeaders
						}
			}

			try {
				const resp = await API[this.statisticExportEndpoint](criteria)
				const blob = window.URL.createObjectURL(new Blob([resp.data], {type: resp.data.type}))
				this.downloadFile(`${this.statisticSelected.name}-export-${SugarDate.Date(new Date()).format("{yyyy}{MM}{dd}").raw}.xlsx`,blob)
			} catch(e) {
				console.log(e)
			}
		},

		//- Function to get items to data-table from API
		async getData() {
			if (!this.clickedSearch && !this.dataFetched) {
				return
			}

			const { sortBy, sortDesc, page } = this.statisticOptions
			const startAt = (page - 1) * this.itemsPerPage

			this.criteria = {
				page: page,
				startAt: startAt || 0,
				sortBy: sortBy[0],
				sortDesc: sortDesc[0],
				searchParameters: this.statisticCriteria,
				pageSize: this.itemsPerPage
			}

			try {
				const resp = await API[this.statisticListEndpoint](this.criteria)
				this.statisticItems = resp.data.rows
				this.totalRows = resp.data.count
				this.dataFetched = true
				// console.log(resp.data)
			} catch(e) {
				console.log(e)
			}
			this.clickedSearch = false
		}
	}
}
</script>

<style>

</style>